import React from "react";
import { Avatar, Badge, Box, Chip, Stack, Typography } from "@mui/material";

function formatTimestamp(timestamp) {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  const now = new Date();
  const diffMinutes = Math.max(0, Math.round((now.getTime() - date.getTime()) / 60000));
  if (diffMinutes < 60) return `${diffMinutes || 1}m`;
  if (diffMinutes < 24 * 60) return `${Math.round(diffMinutes / 60)}h`;
  const diffDays = Math.round(diffMinutes / 1440);
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function getRelationshipChip(thread) {
  if (thread?.activeSprint?.id) return "Active Accountability";
  if (thread?.relationshipState === "past_sprint") return "Past Accountability";
  return "Connected";
}

export default function ChatThreadRow({ thread, selected = false, onClick, onOpenPerson }) {
  const openThread = () => onClick?.(thread);
  const openProfile = (event) => {
    event?.stopPropagation();
    onOpenPerson?.(thread);
  };

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={openThread}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          openThread();
        }
      }}
      sx={{
        width: "100%",
        textAlign: "left",
        borderRadius: "20px",
        border: "1px solid",
        borderColor: selected ? "rgba(23, 52, 93, 0.14)" : "rgba(225, 233, 242, 0.92)",
        backgroundColor: selected ? "#eef4fb" : "#ffffff",
        px: 2,
        py: 1.65,
        cursor: "pointer",
        transition: "background-color 140ms ease, box-shadow 140ms ease",
        "&:hover": {
          backgroundColor: selected ? "#e8f0fa" : "#f8fbff",
        },
        "&:focus-visible": {
          outline: "none",
          boxShadow: "0 0 0 2px rgba(23, 52, 93, 0.16)",
        },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: "100%", minWidth: 0 }}>
        <Box
          role="button"
          tabIndex={0}
          aria-label={`Open ${thread?.partner?.username || "person"} profile`}
          onClick={openProfile}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              openProfile(event);
            }
          }}
          sx={{
            borderRadius: "50%",
            cursor: "pointer",
            "&:focus-visible": {
              outline: "2px solid rgba(23, 52, 93, 0.24)",
              outlineOffset: 3,
            },
          }}
        >
          <Badge
            color="primary"
            overlap="circular"
            variant={thread?.unreadCount > 0 ? "dot" : "standard"}
            sx={{ "& .MuiBadge-badge": { backgroundColor: "#17345d" } }}
          >
            <Avatar src={thread?.partner?.avatar || ""}>
              {(thread?.partner?.username || "T").slice(0, 1).toUpperCase()}
            </Avatar>
          </Badge>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Typography
              role="button"
              tabIndex={0}
              onClick={openProfile}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  openProfile(event);
                }
              }}
              sx={{
                fontWeight: 800,
                color: "#14213d",
                cursor: "pointer",
                "&:hover": { color: "#17345d", textDecoration: "underline" },
                "&:focus-visible": {
                  outline: "2px solid rgba(23, 52, 93, 0.18)",
                  outlineOffset: 2,
                },
              }}
            >
              {thread?.partner?.username || "Conversation"}
            </Typography>
            <Typography variant="caption" sx={{ color: "#718198", fontWeight: 700 }}>
              {formatTimestamp(thread?.lastActivityAt || thread?.lastMessage?.createdAt)}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.6 }}>
            <Chip
              label={getRelationshipChip(thread)}
              size="small"
              sx={{
                height: 22,
                borderRadius: "999px",
                backgroundColor: "#f4f7fb",
                color: "#4f627b",
                fontWeight: 700,
              }}
            />
            {thread?.unreadCount > 0 ? (
              <Typography variant="caption" sx={{ color: "#17345d", fontWeight: 800 }}>
                {thread.unreadCount} new
              </Typography>
            ) : null}
          </Stack>
          <Typography
            variant="body2"
            sx={{
              mt: 0.8,
              color: "#5e7087",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {thread?.lastMessage?.body || "No messages yet"}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}
